/**
 * DocumentUploadModal - Opplasting og AI-analyse av juridiske dokumenter
 * Brukes i alle private moduler for kontrakts-/brevanalyse
 */
import { useState, useRef, useCallback } from 'react';
import { Upload, FileText, X, Loader2, AlertTriangle, CheckCircle, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_LABELS = {
  'husleierett': 'Husleierett',
  'forbrukerrett': 'Forbrukerrett',
  'arbeidsrett': 'Arbeidsrett',
  'arbeidsrett-privat': 'Arbeidsrett',
  'eiendomsrett': 'Eiendomsrett',
  'forvaltningsrett': 'Forvaltningsrett',
  'arverett': 'Arverett',
  'trygderett': 'Trygderett',
};

const FILE_ICONS = {
  '.pdf': 'text-red-400',
  '.docx': 'text-blue-400',
  '.jpg': 'text-green-400',
  '.jpeg': 'text-green-400',
  '.png': 'text-green-400',
};

export function DocumentUploadModal({ moduleId, isOpen, onClose, onDocumentAdded }) {
  const { user, token } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((selectedFile) => {
    const ext = selectedFile.name.toLowerCase().split('.').pop();
    const allowed = ['pdf', 'docx', 'jpg', 'jpeg', 'png'];
    if (!allowed.includes(ext)) {
      toast.error(`Filtypen .${ext} er ikke stottet. Bruk PDF, DOCX, JPG eller PNG.`);
      return;
    }
    if (selectedFile.size > 15 * 1024 * 1024) {
      toast.error('Filen er for stor. Maks 15 MB.');
      return;
    }
    setFile(selectedFile);
    setResult(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  }, [handleFileSelect]);

  const handleUpload = async () => {
    if (!file || !user) return;
    setUploading(true);
    setProgress('Laster opp dokument...');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('module_id', moduleId);
    formData.append('user_id', user.id);
    formData.append('folder_name', `${MODULE_LABELS[moduleId] || moduleId} - Opplastede dokumenter`);

    try {
      setProgress('Analyserer dokument med AI...');
      const res = await fetch(`${API}/api/module-documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Opplasting feilet');
      }

      const data = await res.json();
      setResult(data.document);
      setProgress('');
      toast.success('Dokument analysert og lagret i biblioteket!');
      if (onDocumentAdded) onDocumentAdded(data.document);
      
      // Fire event for Aktiv Medhjelper (AI chat auto-trigger)
      window.dispatchEvent(new CustomEvent('document-uploaded', {
        detail: { document: data.document, moduleId }
      }));
    } catch (err) {
      toast.error(err.message || 'Noe gikk galt');
      setProgress('');
    }
    setUploading(false);
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setProgress('');
    setUploading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="upload-modal">
      <div className="bg-[#1B2631] rounded-xl w-full max-w-2xl border border-[#2C3E50] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#2C3E50]">
          <div>
            <h3 className="text-lg font-semibold text-white">Last opp dokument</h3>
            <p className="text-sm text-[#8899A8]">
              {MODULE_LABELS[moduleId] || moduleId} - AI-analyse av kontrakter og brev
            </p>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-[#2C3E50] rounded" data-testid="upload-modal-close">
            <X className="w-5 h-5 text-[#8899A8]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {!result ? (
            <>
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                  border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                  ${dragOver
                    ? 'border-[#B87333] bg-[#B87333]/10'
                    : file
                    ? 'border-[#22c55e]/50 bg-[#22c55e]/5'
                    : 'border-[#2C3E50] hover:border-[#B87333]/50 hover:bg-[#0F1419]'
                  }
                `}
                data-testid="upload-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                />

                {file ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-3 rounded-full bg-[#22c55e]/20">
                      <FileText className="w-8 h-8 text-[#22c55e]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{file.name}</p>
                      <p className="text-xs text-[#8899A8] mt-1">
                        {(file.size / 1024).toFixed(0)} KB - Klikk for a bytte fil
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-3 rounded-full bg-[#B87333]/20">
                      <Upload className="w-8 h-8 text-[#B87333]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Dra og slipp fil her</p>
                      <p className="text-xs text-[#8899A8] mt-1">eller klikk for a velge fil</p>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {['PDF', 'DOCX', 'JPG', 'PNG'].map(t => (
                        <span key={t} className="text-[10px] px-2 py-0.5 bg-[#2C3E50] text-[#8899A8] rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Info box */}
              <div className="mt-4 p-3 bg-[#B87333]/10 border border-[#B87333]/30 rounded-lg">
                <p className="text-xs text-[#B87333]">
                  Dokumentet blir analysert av en AI-jurist med fokus pa <strong>{MODULE_LABELS[moduleId] || 'juridisk analyse'}</strong>.
                  Analysen lagres i ditt bibliotek. Stotter bilder av skannede dokumenter.
                </p>
              </div>

              {/* Upload progress */}
              {uploading && (
                <div className="mt-4 flex items-center gap-3 p-4 bg-[#0F1419] rounded-lg">
                  <Loader2 className="w-5 h-5 text-[#B87333] animate-spin flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white">{progress}</p>
                    <p className="text-xs text-[#8899A8] mt-1">Dette kan ta opptil 30 sekunder for store dokumenter</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Analysis Result */
            <AnalysisResult result={result} moduleId={moduleId} />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#2C3E50] flex justify-end gap-3">
          {!result ? (
            <>
              <button onClick={handleClose} className="px-4 py-2 text-[#8899A8] hover:text-white text-sm">
                Avbryt
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="flex items-center gap-2 px-5 py-2 bg-[#B87333] hover:bg-[#A06030] text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="upload-submit"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Analyserer...' : 'Last opp og analyser'}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="px-5 py-2 bg-[#B87333] hover:bg-[#A06030] text-white rounded-lg text-sm"
            >
              Lukk
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function AnalysisResult({ result, moduleId }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="space-y-4" data-testid="analysis-result">
      {/* Success header */}
      <div className="flex items-center gap-3 p-4 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg">
        <CheckCircle className="w-5 h-5 text-[#22c55e] flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-[#22c55e]">Analyse fullfort</p>
          <p className="text-xs text-[#8899A8] mt-0.5">
            {result.filename} - {result.analysis_meta?.module_focus || MODULE_LABELS[moduleId]}
          </p>
        </div>
      </div>

      {/* Analysis content */}
      <div className="bg-[#0F1419] rounded-lg border border-[#2C3E50]">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-4 hover:bg-[#1B2631]/50"
        >
          <span className="text-sm font-medium text-white">Juridisk analyse</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-[#8899A8]" /> : <ChevronDown className="w-4 h-4 text-[#8899A8]" />}
        </button>
        {expanded && (
          <div className="px-4 pb-4">
            <div
              className="prose prose-sm prose-invert max-w-none text-[#C8D6E0] leading-relaxed
                [&_h2]:text-[#B87333] [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2
                [&_h3]:text-white [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-3 [&_h3]:mb-1
                [&_strong]:text-white [&_ul]:space-y-1 [&_li]:text-sm
                [&_p]:text-sm [&_p]:mb-2"
              dangerouslySetInnerHTML={{ __html: formatMarkdown(result.analysis) }}
            />
          </div>
        )}
      </div>

      <p className="text-xs text-[#8899A8] text-center">
        Dokumentet er lagret i ditt bibliotek og kan aksesseres senere.
      </p>
    </div>
  );
}


function formatMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/## (.*?)$/gm, '<h2>$1</h2>')
    .replace(/### (.*?)$/gm, '<h3>$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- (.*?)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>')
    .replace(/^(.+?)$/gm, (match) => {
      if (match.startsWith('<')) return match;
      return `<p>${match}</p>`;
    });
}


export default DocumentUploadModal;
